import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Plus, ShoppingCart, CalendarDays, ShieldAlert, BarChart3 } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { PageHeader } from '../components/PageHeader';
import PoExportButton from '../components/purchasing/PoExportButton';
import PoBillsExportButton from '../components/purchasing/PoBillsExportButton';
import PoSpendDashboard from '../components/purchasing/PoSpendDashboard';
import { PoStatusBadge, PoTypeChip, money, localToday } from '../components/purchasing/poShared';

const STATUSES = ['', 'Draft', 'Pending Approval', 'Rejected', 'Issued', 'Partially Received', 'Received', 'Closed', 'Canceled', 'Void'];

const PoRow = ({ po }) => (
  <Link to={`/purchasing/pos/${po.id}`} className="grid grid-cols-12 gap-2 items-center px-4 py-2.5 border-t border-slate-100 hover:bg-slate-50 text-sm" data-testid={`po-row-${po.id}`}>
    <span className="col-span-2 mono font-semibold text-orange-700">{po.po_number}</span>
    <span className="col-span-2"><PoTypeChip po={po} /></span>
    <span className="col-span-3 truncate">{po.vendor_name_snapshot}</span>
    <span className="col-span-1 mono text-xs">{po.order_date}</span>
    <span className="col-span-1 mono text-xs text-slate-500">{po.expected_date || '—'}</span>
    <span className="col-span-1 mono text-right font-semibold">{money(po.total)}</span>
    <span className="col-span-2 text-right"><PoStatusBadge status={po.status} /></span>
  </Link>
);

const TodayBoard = ({ pos, day }) => {
  const groups = useMemo(() => pos.reduce((a, p) => ({ ...a, [p.vendor_name_snapshot]: [...(a[p.vendor_name_snapshot] || []), p] }), {}), [pos]);
  if (!pos.length) return <p className="text-sm text-slate-400 py-8 text-center" data-testid="po-today-empty">No purchase orders dated {day}.</p>;
  return (
    <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-3" data-testid="po-today-board">
      {Object.entries(groups).map(([vendor, list]) => (
        <Card key={vendor} className={`industrial-card ${list.length > 1 ? 'ring-2 ring-amber-300' : ''}`} data-testid={`po-today-vendor-${vendor}`}>
          <CardContent className="p-3">
            <div className="flex items-center justify-between mb-2">
              <span className="font-semibold truncate">{vendor}</span>
              <span className="text-xs mono">{list.length} PO{list.length > 1 ? 's' : ''} · {money(list.reduce((s, p) => s + p.total, 0))}</span>
            </div>
            {list.length > 1 && <p className="text-[11px] text-amber-700 mb-1.5">Multiple POs to this vendor today. Consider combining open drafts.</p>}
            {list.map(p => (
              <Link key={p.id} to={`/purchasing/pos/${p.id}`} className="flex items-center justify-between text-sm py-1 border-t border-slate-100 hover:text-orange-700">
                <span className="flex items-center gap-2"><span className="mono">{p.po_number}</span><PoTypeChip po={p} /></span>
                <span className="flex items-center gap-2"><span className="mono">{money(p.total)}</span><PoStatusBadge status={p.status} /></span>
              </Link>
            ))}
          </CardContent>
        </Card>
      ))}
    </div>
  );
};

export default function PurchaseOrdersPage() {
  const { api } = useAuth();
  const navigate = useNavigate();
  const [pos, setPos] = useState([]);
  const [types, setTypes] = useState([]);
  const [tab, setTab] = useState('today');
  const [day, setDay] = useState(localToday());
  const [type, setType] = useState('');
  const [status, setStatus] = useState('');

  const load = useCallback(async () => {
    try {
      const [p, s] = await Promise.all([api().get('/purchase-orders'), api().get('/purchase-orders/settings')]);
      setPos(p.data || []); setTypes(s.data.types || []);
    } catch { toast.error('Failed to load purchase orders'); }
  }, [api]);
  useEffect(() => { load(); }, [load]);

  const filtered = pos.filter(p => (!type || p.po_type === type) && (!status || p.status === status));
  const pending = pos.filter(p => p.status === 'Pending Approval').length;
  const onOrder = pos.filter(p => ['Issued', 'Partially Received'].includes(p.status)).reduce((s, p) => s + p.total, 0);

  return (
    <div className="space-y-4" data-testid="purchase-orders-page">
      <PageHeader crumbs={[{ label: 'Buy' }, { label: 'Purchase Orders' }]} icon={ShoppingCart} title="Purchase Orders" description="Vendor POs by type. Incoming stock shows as On Order until received."
        actions={<div className="flex gap-2"><PoExportButton api={api} /><PoBillsExportButton api={api} /><Button onClick={() => navigate('/purchasing/pos/new')} className="bg-slate-900 hover:bg-slate-800 rounded-sm" data-testid="po-new-btn"><Plus className="w-4 h-4 mr-1" /> New PO</Button></div>} />
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Stat label="Open (on order)" value={money(onOrder)} testId="po-stat-open" />
        <Stat label="Pending approval" value={pending} testId="po-stat-pending" warn={pending > 0} onClick={() => { setTab('all'); setStatus('Pending Approval'); }} />
        <Stat label="POs today" value={pos.filter(p => p.order_date === localToday() && !['Canceled', 'Void'].includes(p.status)).length} testId="po-stat-today" />
        <Stat label="Total POs" value={pos.length} testId="po-stat-total" />
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <Button size="sm" variant={tab === 'today' ? 'default' : 'outline'} onClick={() => setTab('today')} className="rounded-sm" data-testid="po-tab-today"><CalendarDays className="w-3.5 h-3.5 mr-1" /> Day board</Button>
        <Button size="sm" variant={tab === 'all' ? 'default' : 'outline'} onClick={() => setTab('all')} className="rounded-sm" data-testid="po-tab-all"><ShoppingCart className="w-3.5 h-3.5 mr-1" /> All POs</Button>
        <Button size="sm" variant={tab === 'spend' ? 'default' : 'outline'} onClick={() => setTab('spend')} className="rounded-sm" data-testid="po-tab-spend"><BarChart3 className="w-3.5 h-3.5 mr-1" /> Spend</Button>
        {tab === 'today' && <Input type="date" value={day} onChange={(e) => setDay(e.target.value)} className="w-40 h-8 rounded-sm" data-testid="po-day-input" />}
        {tab === 'all' && (
          <>
            <select value={type} onChange={(e) => setType(e.target.value)} className="h-8 border border-slate-200 rounded-sm px-2 text-sm" data-testid="po-filter-type">
              <option value="">All types</option>{types.map(t => <option key={t.key} value={t.key}>{t.label}</option>)}
            </select>
            <select value={status} onChange={(e) => setStatus(e.target.value)} className="h-8 border border-slate-200 rounded-sm px-2 text-sm" data-testid="po-filter-status">
              {STATUSES.map(s => <option key={s} value={s}>{s || 'All statuses'}</option>)}
            </select>
          </>
        )}
      </div>
      {tab === 'spend' ? <PoSpendDashboard api={api} pos={pos} /> : tab === 'today' ? <TodayBoard pos={pos.filter(p => p.order_date === day && !['Canceled', 'Void'].includes(p.status))} day={day} /> : (
        <Card className="industrial-card"><CardContent className="p-0" data-testid="po-list">
          <div className="grid grid-cols-12 gap-2 px-4 py-2 text-[10px] uppercase tracking-wider text-slate-500">
            <span className="col-span-2">PO #</span><span className="col-span-2">Type</span><span className="col-span-3">Vendor</span><span className="col-span-1">Date</span><span className="col-span-1">Expected</span><span className="col-span-1 text-right">Total</span><span className="col-span-2 text-right">Status</span>
          </div>
          {filtered.map(p => <PoRow key={p.id} po={p} />)}
          {!filtered.length && <p className="text-sm text-slate-400 px-4 py-6 text-center">No purchase orders match.</p>}
        </CardContent></Card>
      )}
    </div>
  );
}

const Stat = ({ label, value, testId, warn, onClick }) => (
  <button type="button" onClick={onClick} className={`text-left border rounded-sm px-4 py-3 bg-white ${warn ? 'border-amber-400 bg-amber-50' : 'border-slate-200'} ${onClick ? 'hover:border-slate-400' : 'cursor-default'}`} data-testid={testId}>
    <div className="text-[10px] uppercase tracking-wider text-slate-500 flex items-center gap-1">{warn && <ShieldAlert className="w-3 h-3 text-amber-600" />}{label}</div>
    <div className="text-xl font-bold mono mt-0.5">{value}</div>
  </button>
);
